import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-level cache shared across all consumers (single fetch per app lifetime)
let cache = null;
let inflight = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn(cache));

const fetchBranding = async () => {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = axios
    .get(`${API}/settings`)
    .then((res) => {
      cache = {
        logoMain: res.data?.logo_main || '',
        logoSmall: res.data?.logo_small || '',
      };
      notify();
      return cache;
    })
    .catch(() => {
      cache = { logoMain: '', logoSmall: '' };
      notify();
      return cache;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

/**
 * Force refresh (called by admin Settings after save to update across app without reload)
 */
export const refreshBranding = async () => {
  cache = null;
  return fetchBranding();
};

/**
 * Hook returning { logoMain, logoSmall } — safe fallbacks to empty strings.
 * Consumers should fall back to the default "W" mark when values are empty.
 */
export const useBranding = () => {
  const [branding, setBranding] = useState(cache || { logoMain: '', logoSmall: '' });

  useEffect(() => {
    let active = true;
    const onUpdate = (v) => active && setBranding(v);
    listeners.add(onUpdate);
    if (!cache) fetchBranding();
    return () => {
      active = false;
      listeners.delete(onUpdate);
    };
  }, []);

  return branding;
};

/**
 * Injects/updates a <link rel="icon"> in the document head.
 * Called once by App on mount — keeps favicon in sync with logo_small.
 */
export const applyFavicon = (href) => {
  if (typeof document === 'undefined') return;
  const head = document.head;
  let link = head.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    head.appendChild(link);
  }
  link.href = href || '/favicon.ico';
};
